import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Phone, Sparkles, Shield, Award, Clock, Heart, Coins, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { fetchCollection, FALLBACK_DATA } from '../lib/cms';
import Beregner from '../components/Beregner';
import GalleryModal from '../components/GalleryModal';
import SEO from '../components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

export default function Home() {
  const [testimonials, setTestimonials] = useState(FALLBACK_DATA.testimonials || []);
  const [galleryImages, setGalleryImages] = useState(FALLBACK_DATA.galleri[0]?.images || []);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [loadVideo, setLoadVideo] = useState(true);

  useEffect(() => {
    // Delay heavy hero video load until after first paint or user interaction
    const timer = setTimeout(() => {
      setLoadVideo(true);
    }, 1500);

    const handleInteraction = () => {
      setLoadVideo(true);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('click', handleInteraction);
    };

    window.addEventListener('scroll', handleInteraction, { passive: true });
    window.addEventListener('touchstart', handleInteraction, { passive: true });
    window.addEventListener('click', handleInteraction, { passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('click', handleInteraction);
    };
  }, []);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleCount(1);
      } else if (window.innerWidth < 1024) {
        setVisibleCount(2);
      } else {
        setVisibleCount(3);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, testimonials.length - visibleCount);

  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [visibleCount, maxIndex, currentIndex]);

  useEffect(() => {
    if (testimonials.length === 0 || isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 6000);
    return () => clearInterval(interval);
  }, [testimonials, isPaused, maxIndex]);

  const nextSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  useEffect(() => {
    async function loadData() {
      const testData = await fetchCollection('testimonials');
      setTestimonials(testData);

      const galleriData = await fetchCollection('galleri');
      if (galleriData && galleriData.length > 0) {
        setGalleryImages(galleriData[0].images || []);
      }
    }
    loadData();
  }, []);

  const openGallery = (index) => {
    setGalleryIndex(index);
    setIsGalleryOpen(true);
  };

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Danshaker - Effektiv og Ergonomisk Sorteringsmaskine',
    description: 'Danshaker er udviklet til at lette det tunge og slidsomme sorteringsarbejde i stalden. Spar op til 50% tid og strøelse.',
    url: 'https://danshaker.dk/',
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-50 text-slate-900 antialiased">
      <SEO
        title="Halvér arbejdet i stalden"
        description="Danshaker er den originale danske sorteringsmaskine. Spar op til 50% tid, halvér forbruget af strøelse og skån kroppen. Book en gratis demo i din stald i dag."
        path="/"
        schema={homeSchema}
      />

      {/* Decorative background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />
      <div className="pointer-events-none absolute top-[30%] left-[-10%] h-[600px] w-[600px] rounded-full bg-[#0E67A2]/2 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-[10%] right-[-10%] h-[600px] w-[600px] rounded-full bg-[#0E67A2]/2 blur-[120px]" />

      {/* Hero Section */}
      <section className="relative mx-auto max-w-7xl px-4 pt-8 pb-10 md:px-8 md:pt-12 lg:pt-16 md:pb-14 lg:pb-16">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
          {/* Left Content */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="space-y-8 lg:col-span-6"
          >
            <motion.div 
              variants={fadeInUp}
              className="inline-flex items-center gap-2.5 rounded-full border border-[#0E67A2]/20 bg-[#0E67A2]/5 px-5 py-2 text-sm font-bold text-[#0E67A2]"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              <span>Den originale danske sorteringsmaskine</span>
            </motion.div>

            <div className="space-y-4">
              <motion.h1 variants={fadeInUp} className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl md:text-6xl lg:text-7xl leading-none">
                Halvér arbejdet<br />
                <span className="bg-gradient-to-r from-[#0E67A2] to-sky-600 bg-clip-text text-transparent">i stalden!</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="max-w-2xl text-lg md:text-xl text-slate-800 leading-relaxed font-medium">
                Danshaker er udviklet til at lette det tunge og slidsomme sorteringsarbejde. Spar op til 50% af din tid, halvér dit forbrug af strøelse og undgå fysisk overbelastning.
              </motion.p>
            </div>

            {/* CTA Buttons */}
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Link
                to="/gratis-demo"
                className="flex items-center gap-2.5 rounded-xl bg-[#0E67A2] px-6 py-3.5 text-base font-bold text-white shadow-lg shadow-[#0E67A2]/10 transition duration-300 hover:scale-[1.02] hover:bg-[#0E67A2]/90"
              >
                <Calendar className="h-5 w-5" aria-hidden="true" />
                Book gratis demo
              </Link>
              <Link
                to="/produkt"
                className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-base font-bold text-slate-700 shadow-sm transition duration-300 hover:scale-[1.02] hover:bg-slate-50"
              >
                Se produkter
              </Link>
              <Link
                to="/kontakt"
                className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-base font-bold text-slate-700 shadow-sm transition duration-300 hover:scale-[1.02] hover:bg-slate-50"
              >
                Kontakt os
              </Link>
            </motion.div>

            {/* Trust Badges */}
            <motion.div variants={fadeInUp} className="grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-slate-200 pt-10 text-slate-500">
              <div className="flex items-center gap-3">
                <Shield className="h-6 w-6 text-[#0E67A2] shrink-0" aria-hidden="true" />
                <span className="text-sm font-semibold text-slate-700">Dansk design & produktion</span>
              </div>
              <div className="flex items-center gap-3">
                <Award className="h-6 w-6 text-[#0E67A2] shrink-0" aria-hidden="true" />
                <span className="text-sm font-semibold text-slate-700">Kvalitetsmaterialer</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-6 w-6 text-[#0E67A2] shrink-0" aria-hidden="true" />
                <span className="text-sm font-semibold text-slate-700">Hurtig levering</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Image/Visual */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            className="relative lg:col-span-6"
          >
            <div className="relative mx-auto max-w-[750px] lg:max-w-none">
              {/* Glow effect behind video */}
              <div className="absolute -inset-6 rounded-3xl bg-[#0E67A2]/5 blur-3xl" />
              <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-4 shadow-xl">
                {loadVideo && (
                  <video
                    src="https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/dd23b170-9451-48c1-be2e-929bf23525fb.mov"
                    poster="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/82c2389c-2509-456f-8e9c-aa5c44a15f3e.jpg"
                    className="w-full h-auto max-h-[600px] mx-auto rounded-2xl shadow-2xl bg-black"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                  />
                )}
                <div className="absolute bottom-6 right-6 rounded-xl bg-white/95 px-4 py-2 text-xs font-bold text-slate-900 shadow-lg border border-slate-200">
                  Se Danshaker i aktion
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Value Propositions (Tid, Strøelse, Krop) */}
      <section className="border-y border-slate-200 bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-black text-slate-950 md:text-4xl">Hvorfor vælge en Danshaker?</h2>
            <p className="mt-4 text-base text-slate-900 font-semibold">
              Danshaker optimerer dit arbejdsmiljø i stalden og reducerer dine omkostninger markant.
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {/* Spar Tid */}
            <motion.div variants={fadeInUp} className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-8 text-center shadow-md transition hover:border-[#0E67A2]/40 hover:shadow-lg">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0E67A2]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=120,quality=85,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/a792de19-260d-4ca8-9514-ca552cf7c662.png"
                  alt="Spar Tid ikon"
                  width="40"
                  height="40"
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-10 object-contain"
                />
              </div>
              <h3 className="text-xl font-black text-slate-950 mb-2">Spar TID</h3>
              <p className="text-[#0E67A2] font-black mb-4">Op til 50% hurtigere sortering</p>
              <p className="text-sm text-slate-900 font-semibold leading-relaxed">
                Danshaker har reduceret sorteringstiden for flere af vores kunder med over 50%. Få mere tid til dine heste.
              </p>
            </motion.div>

            {/* Spar Strøelse */}
            <motion.div variants={fadeInUp} className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-8 text-center shadow-md transition hover:border-[#0E67A2]/40 hover:shadow-lg">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0E67A2]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=120,quality=85,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/feb939a4-8f35-4c2d-a66d-e8556b06f45a.png"
                  alt="Spar Strøelse ikon"
                  width="40"
                  height="40"
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-10 object-contain"
                />
              </div>
              <h3 className="text-xl font-black text-slate-950 mb-2">Spar STRØELSE</h3>
              <p className="text-[#0E67A2] font-black mb-4">Op til 50% besparelse</p>
              <p className="text-sm text-slate-900 font-semibold leading-relaxed">
                Rens strøelsen på få sekunder og genbrug det rene materiale. Smid kun møget ud og halvér dine udgifter.
              </p>
            </motion.div>

            {/* Spar Kroppen */}
            <motion.div variants={fadeInUp} className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-8 text-center shadow-md transition hover:border-[#0E67A2]/40 hover:shadow-lg md:col-span-2 lg:col-span-1">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#0E67A2]">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=120,quality=85,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/17efd1be-1500-4bfa-a02d-0792317ba244.png"
                  alt="Spar Kroppen ikon"
                  width="40"
                  height="40"
                  loading="lazy"
                  decoding="async"
                  className="h-10 w-10 object-contain"
                />
              </div>
              <h3 className="text-xl font-black text-slate-950 mb-2">Spar KROPPEN</h3>
              <p className="text-[#0E67A2] font-black mb-4">Undgå fysisk overbelastning</p>
              <p className="text-sm text-slate-900 font-semibold leading-relaxed">
                Danshakeren laver alt det hårde sorteringsarbejde, så du slipper for de belastende og repetitive mugebevægelser.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Key Features List */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">Effektiv og gennemtænkt teknologi</h2>
            <p className="text-slate-700 font-medium">
              Danshaker er udviklet med fokus på funktionalitet, holdbarhed og brugervenlighed i det daglige staldmiljø.
            </p>
            <ul className="space-y-4">
              {[
                'Danshaker har reduceret sorteringstiden for flere af vores kunder med over 50%',
                'Danshaker kører på genopladeligt batteri = ingen forlængerledninger hen ad staldgangen',
                'Danshaker kan klare op til 86 bokssorteringer på en fuld opladning',
                'Danshaker optimerer dit arbejdsmiljø i stalden og reducerer dine omkostninger til dyre fysiologiske behandlinger'
              ].map((point, idx) => (
                <li key={idx} className="flex items-start gap-3 text-slate-800 font-medium">
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#0E67A2]/10 text-[#0E67A2] font-bold text-xs" aria-hidden="true">✓</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            <div className="pt-4">
              <Link
                to="/gratis-demo"
                className="inline-flex items-center gap-2 rounded-xl bg-[#0E67A2] px-6 py-3 font-bold text-white transition hover:bg-[#0E67A2]/90"
              >
                Book en gratis demo
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/82c2389c-2509-456f-8e9c-aa5c44a15f3e.jpg"
              alt="Smid boksbunden op i Danshakeren"
              width="600"
              height="338"
              loading="lazy"
              decoding="async"
              className="rounded-xl border border-slate-200 object-cover w-full aspect-video shadow-sm"
            />
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/69ce56ce-163d-4516-8f4f-6b6ab388a8f4.jpg"
              alt="Danshakeren sorterer effektivt"
              width="600"
              height="338"
              loading="lazy"
              decoding="async"
              className="rounded-xl border border-slate-200 object-cover w-full aspect-video shadow-sm"
            />
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/6043796d-21d0-49bc-ab4e-1aa2283c1f84.jpg"
              alt="Genbrug den rene strøelse efter sortering"
              width="600"
              height="338"
              loading="lazy"
              decoding="async"
              className="rounded-xl border border-slate-200 object-cover w-full aspect-video shadow-sm"
            />
            <div className="flex flex-col justify-center rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm">
              <span className="text-3xl font-extrabold text-[#0E67A2]">10/10</span>
              <span className="text-xs text-slate-700 font-bold uppercase tracking-wider mt-1">Stjerner fra kunder</span>
            </div>
          </div>
        </div>
      </section>

      {/* Savings Calculator Section */}
      <section className="border-t border-slate-300 bg-white py-12">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">Hvor meget kan du spare?</h2>
            <p className="mt-4 text-slate-700 font-medium">
              Brug vores interaktive besparelsesberegner til at se, hvor hurtigt en Danshaker tjener sig selv hjem i din stald.
            </p>
          </div>

          <Beregner />
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="border-t border-slate-300 py-12">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">Hvad siger vores kunder?</h2>
              <p className="mt-4 text-slate-700 font-medium">
                Læs udtalelser fra professionelle stalde og private, der bruger Danshakeren dagligt.
              </p>
            </div>

            <div 
              className="relative overflow-hidden"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div 
                className="flex flex-nowrap transition-transform duration-500 ease-in-out gap-6"
                style={{ 
                  transform: `translateX(calc(-${currentIndex} * (100% + 1.5rem) / ${visibleCount}))` 
                }}
              >
                {testimonials.map((item) => (
                  <div 
                    key={item.id} 
                    className="flex flex-col justify-between rounded-2xl border border-slate-300 bg-white p-6 shadow-md transition hover:border-[#0E67A2]/20 w-full shrink-0 md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]"
                  >
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        {item.image && item.image[0] ? (
                          <img
                            src={item.image[0]}
                            alt={item.name}
                            width="48"
                            height="48"
                            loading="lazy"
                            decoding="async"
                            className="h-12 w-12 rounded-full object-cover border border-slate-300"
                          />
                        ) : (
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#0E67A2]/10 text-[#0E67A2] font-extrabold text-lg border border-[#0E67A2]/20">
                            {item.name ? item.name.charAt(0) : 'K'}
                          </div>
                        )}
                        <div>
                          <h3 className="font-bold text-slate-900 text-base">{item.name}</h3>
                          <p className="text-xs text-slate-700 font-semibold">{item.company}</p>
                          <div className="flex items-center gap-1 text-amber-400 text-xs mt-0.5" aria-label="5 ud af 5 stjerner">
                            <i className="fa-solid fa-star" />
                            <i className="fa-solid fa-star" />
                            <i className="fa-solid fa-star" />
                            <i className="fa-solid fa-star" />
                            <i className="fa-solid fa-star" />
                          </div>
                        </div>
                      </div>
                      <div
                        className="text-sm text-slate-800 leading-relaxed italic font-medium"
                        dangerouslySetInnerHTML={{ __html: item.text }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {maxIndex > 0 && (
              <div className="flex items-center justify-center gap-6 mt-10">
                <button
                  onClick={prevSlide}
                  className="p-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-[#0E67A2] transition-colors shadow-sm cursor-pointer"
                  aria-label="Forrige udtalelse"
                >
                  <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                </button>

                <div className="flex gap-2">
                  {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                        idx === currentIndex ? 'w-8 bg-[#0E67A2]' : 'w-2.5 bg-slate-200 hover:bg-slate-300'
                      }`}
                      aria-label={`Gå til udtalelse ${idx + 1}`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextSlide}
                  className="p-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-[#0E67A2] transition-colors shadow-sm cursor-pointer"
                  aria-label="Næste udtalelse"
                >
                  <ChevronRight className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            )}

            <div className="mt-12 text-center">
              <Link
                to="/kundeudtalelser"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3 font-bold text-[#0E67A2] shadow-sm transition hover:bg-slate-50"
              >
                Læs alle kundeudtalelser
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="border-t border-slate-300 py-12">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">Oplev Danshakeren helt tæt på</h2>
              <p className="mt-4 text-slate-700 font-medium">
                Klik på billederne for at se detaljerne af maskinen og dens tilbehør i stor visning.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7">
              {galleryImages.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => openGallery(idx)}
                  className="group relative aspect-square cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition hover:border-[#0E67A2]/30"
                >
                  <img
                    src={img}
                    alt={`Danshaker detalje ${idx + 1}`}
                    width="300"
                    height="300"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-slate-950/20 opacity-0 transition group-hover:opacity-100 flex items-center justify-center">
                    <span className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#0E67A2] border border-slate-200 shadow-md">Forstør</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA Section */}
      <section className="border-t border-slate-300 bg-gradient-to-b from-white to-slate-100 py-12">
        <div className="mx-auto max-w-5xl px-4 text-center md:px-8">
          <h2 className="text-3xl font-extrabold text-slate-900 md:text-5xl leading-tight">
            Klar til at gøre hverdagen<br />
            <span className="bg-gradient-to-r from-[#0E67A2] to-sky-600 bg-clip-text text-transparent">i stalden lettere?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate-800 font-medium">
            Book en helt gratis og uforpligtende demo hjemme i din egen stald. Vi viser dig præcis, hvordan maskinen fungerer, og du får selv lov til at prøve den.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/gratis-demo"
              className="flex items-center gap-2 rounded-xl bg-[#0E67A2] px-8 py-4 font-bold text-white shadow-lg shadow-[#0E67A2]/20 transition hover:bg-[#0E67A2]/90"
            >
              <Calendar className="h-5 w-5" aria-hidden="true" />
              Book gratis demo nu
            </Link>
            <Link
              to="/kontakt"
              className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-8 py-4 font-bold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Kontakt os for rådgivning
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <GalleryModal
        isOpen={isGalleryOpen}
        images={galleryImages}
        currentIndex={galleryIndex}
        onClose={() => setIsGalleryOpen(false)}
        onPrev={() => setGalleryIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
        onNext={() => setGalleryIndex((prev) => (prev + 1) % galleryImages.length)}
      />
    </div>
  );
}
